import { createRootRoute, createRoute, createRouter } from '@tanstack/react-router';
import type { JSX } from 'react';

import AdminRoute from './components/admin-route';
import AdminOrAiOperatorRoute from './components/admin-or-ai-operator-route';
import ProtectedRoute from './components/protected-route';
import RoleRoute from './components/role-route';
import AppShell from './components/shell/app-shell';
import { RolesProvider, type Role } from './hooks/use-roles';
import { ExamImportJobsProvider } from './hooks/use-exam-import-jobs';
import { BulkImportJobsProvider } from './hooks/use-bulk-import-jobs';
import Admin from './pages/admin';
import AdminUsers from './pages/admin-users';
import AdminUsersSchools from './pages/admin-users-schools';
import AdminUsersSchoolDetail from './pages/admin-users-school-detail';
import AdminUsersSchoolCreate from './pages/admin-users-school-create';
import AdminUsersAnnuaire from './pages/admin-users-annuaire';
import AdminUsersAcces from './pages/admin-users-acces';
import AdminChat from './pages/admin-chat';
import AdminContent from './pages/admin-content';
import AdminContentDetail from './pages/admin-content-detail';
import AdminCurriculum from './pages/admin-curriculum';
import AdminCurriculumRef from './pages/admin-curriculum-ref';
import AdminDocs from './pages/admin-docs';
import AdminMissingProgrammes from './pages/admin-missing-programmes';
import AdminCouverture from './pages/admin-couverture';
import AdminItemsSearch from './pages/admin-items-search';
import AdminProgramme from './pages/admin-programme';
import AdminPrompts from './pages/admin-prompts';
import AdminLlmUsage from './pages/admin-llm-usage';
import AdminConversations from './pages/admin-conversations';
import AdminSafeguarding from './pages/admin-safeguarding';
import AdminSafeguardingDetail from './pages/admin-safeguarding-detail';
import AdminConversationDetail from './pages/admin-conversation-detail';
import Chat from './pages/chat';
import DirectionClass from './pages/direction-class';
import DirectionContent from './pages/direction-content';
import DirectionContentDetail from './pages/direction-content-detail';
import DirectionHome from './pages/direction-home';
import DirectorOnboarding from './pages/director-onboarding';
import DirectionParentsEleves from './pages/direction-parents-eleves';
import DirectionTeachers from './pages/direction-teachers';
import DirectionGroups from './pages/direction-groups';
import DirectionTeam from './pages/direction-team';
import DirectionMyClasses from './pages/direction-my-classes';
import DirectionMemberClass from './pages/direction-member-class';
import DirectionSupervision from './pages/direction-supervision';
import DirectionAssignmentDetail from './pages/direction-assignment-detail';
import DirectionExamSessionDetail from './pages/direction-exam-session-detail';
import Aide from './pages/aide';
import AppRedirect from './pages/home';
import Landing from './pages/landing';
import ErrorScreen from './pages/error-screen';
import NotFound from './pages/not-found';
import TestError from './pages/test-error';
import ResetPassword from './pages/reset-password';
import PrivacyPolicy from './pages/legal/privacy-policy';
import Terms from './pages/legal/terms';
import Settings from './pages/settings';
import DesignSystemShowcase from './pages/design-system-showcase';
import SignIn from './pages/sign-in';
import TeacherDashboard from './pages/mini-apps/teacher-dashboard';
import TeacherAssistant from './pages/teacher-assistant';
import ContentLibrary from './pages/content-library';
import Assignments from './pages/assignments';
import StudentAssignments from './pages/student-assignments';
import TeacherAssignmentDetail from './pages/teacher-assignment-detail';
import Examens from './pages/examens';
import ExamSession from './pages/exam-session';
import ExamImportReview from './pages/exam-import-review';
import ResponsableAccept from './pages/responsable-accept';
import ResponsableHome from './pages/responsable-home';
import ResponsableChild from './pages/responsable-child';
import InteractiveParcours from './pages/interactive-parcours';
import StudentChat from './pages/student-chat';
import StudentConversations from './pages/student-conversations';
import ContentEditor from './pages/content-editor';
import TeacherClassDetail from './pages/teacher-class-detail';
import TeacherOnboardingWelcome from './pages/onboarding/welcome';
import TeacherOnboardingClasses from './pages/onboarding/classes';
import TeacherOnboardingPrefs from './pages/onboarding/preferences';
import AdminInviteAccept from './pages/admin-invite-accept';
import InviteAccept from './pages/invite-accept';
import MiniApps from './pages/mini-apps';
import Competences from './pages/mini-apps/competences';
import CapApp from './pages/mini-apps/cap';
import FluenceDirection from './pages/mini-apps/fluence-direction';
import FluenceEleve from './pages/mini-apps/fluence-eleve';
import FluenceEleveDetail from './pages/mini-apps/fluence-eleve-detail';
import FluenceEnseignant from './pages/mini-apps/fluence-enseignant';
import FrancaisPrimaireApp from './pages/mini-apps/francais-primaire';
import MathsPrimaireApp from './pages/mini-apps/maths-primaire';
import HgCollegeQcmApp from './pages/mini-apps/hg-college-qcm';
import HgCollegeEnseignant from './pages/mini-apps/hg-college-enseignant';
import FrancaisCollegeApp from './pages/mini-apps/francais-college';
import FrancaisCollegeBilanApp from './pages/mini-apps/francais-college/bilan';
import FrancaisCollegeDomaineApp from './pages/mini-apps/francais-college/domaine';
import FrancaisCollegeHoteApp from './pages/mini-apps/francais-college/hote-iframe';
import FrancaisCollegeFeuilletonApp from './pages/mini-apps/francais-college/feuilleton';
import FrancaisCollegeFeuilleton2App from './pages/mini-apps/francais-college/feuilleton-2';
import FrancaisCollegeRelectureComprehensionApp from './pages/mini-apps/francais-college/relecture-comprehension';
import FrancaisCollegeRelectureLexiqueApp from './pages/mini-apps/francais-college/relecture-lexique';
import Parcours from './pages/mini-apps/parcours';
import Conversations from './pages/conversations';
import PendingAccount from './pages/pending-account';

/* Full app route tree on TanStack Router (replaces react-router-dom). Auth guards */
/* stay component wrappers (ProtectedRoute/AdminRoute). RolesProvider sits at the */
/* root so every route has the roles context. Search is passed through untyped so */
/* `?c=…` thread params keep working via the routing shim (lib/routing). */
const rootRoute = createRootRoute({
  component: () => (
    <RolesProvider>
      <ExamImportJobsProvider>
        <BulkImportJobsProvider>
          <AppShell />
        </BulkImportJobsProvider>
      </ExamImportJobsProvider>
    </RolesProvider>
  ),
  notFoundComponent: NotFound,
  errorComponent: ErrorScreen,
  validateSearch: (search: Record<string, unknown>) => search,
});

const r = (path: string, component: () => JSX.Element) => createRoute({ getParentRoute: () => rootRoute, path, component });
const protectedRoute = (path: string, Page: () => JSX.Element) =>
  r(path, () => (
    <ProtectedRoute>
      <Page />
    </ProtectedRoute>
  ));
const adminRoute = (path: string, Page: () => JSX.Element) =>
  r(path, () => (
    <AdminRoute>
      <Page />
    </AdminRoute>
  ));
/* Sous-ensemble ouvert au rôle ai_operator (lecture seule) — voir
   docs/journals pour le scope exact validé avec Ismaël (12/10). */
const adminOrAiOperatorRoute = (path: string, Page: () => JSX.Element) =>
  r(path, () => (
    <AdminOrAiOperatorRoute>
      <Page />
    </AdminOrAiOperatorRoute>
  ));
const roleRoute = (path: string, Page: () => JSX.Element, role: Role) =>
  r(path, () => (
    <RoleRoute role={role}>
      <Page />
    </RoleRoute>
  ));
const teacherRoute = (path: string, Page: () => JSX.Element) => roleRoute(path, Page, 'teacher');
const directionRoute = (path: string, Page: () => JSX.Element) => roleRoute(path, Page, 'direction');
const tutorRoute = (path: string, Page: () => JSX.Element) => roleRoute(path, Page, 'tutor');

const routes = [
  r('/', Landing),
  r('/sign-in', SignIn),
  r('/reset-password', ResetPassword),
  r('/confidentialite', PrivacyPolicy),
  r('/cgu', Terms),
  r('/invite/accept', InviteAccept),
  r('/admin-invite/accept', AdminInviteAccept),
  r('/tutor/accept', ResponsableAccept),
  r('/responsable/accept', ResponsableAccept), /* backward compat for email links already sent */
  protectedRoute('/app', AppRedirect),
  protectedRoute('/app/pending', PendingAccount),
  protectedRoute('/c/$threadId', Chat),
  protectedRoute('/settings', Settings),
  protectedRoute('/aide', Aide),
  protectedRoute('/design-system', DesignSystemShowcase),
  directionRoute('/direction', DirectionHome),
  directionRoute('/direction/onboarding', DirectorOnboarding),
  directionRoute('/direction/classe/$id', DirectionClass),
  directionRoute('/direction/enseignants', DirectionTeachers),
  directionRoute('/direction/contenus', DirectionContent),
  directionRoute('/direction/contenus/$id', DirectionContentDetail),
  directionRoute('/direction/groupes', DirectionGroups),
  directionRoute('/direction/equipe', DirectionTeam),
  directionRoute('/direction/mes-classes', DirectionMyClasses),
  directionRoute('/direction/mes-classes/$id', DirectionMemberClass),
  directionRoute('/direction/parents-eleves', DirectionParentsEleves),
  directionRoute('/direction/supervision', DirectionSupervision),
  directionRoute('/direction/supervision/devoirs/$id', DirectionAssignmentDetail),
  directionRoute('/direction/supervision/examens/$id', DirectionExamSessionDetail),
  protectedRoute('/onboarding', TeacherOnboardingWelcome),
  protectedRoute('/onboarding/classes', TeacherOnboardingClasses),
  protectedRoute('/onboarding/preferences', TeacherOnboardingPrefs),
  teacherRoute('/teacher', TeacherDashboard),
  teacherRoute('/teacher/chat', TeacherAssistant),
  teacherRoute('/teacher/library', ContentLibrary),
  teacherRoute('/teacher/library/$id', ContentEditor),
  teacherRoute('/teacher/assignments', Assignments),
  teacherRoute('/teacher/assignments/$id', TeacherAssignmentDetail),
  teacherRoute('/teacher/exams', Examens),
  teacherRoute('/teacher/exams/import/$documentId', ExamImportReview),
  teacherRoute('/teacher/exams/$id', ExamSession),
  teacherRoute('/teacher/classes/$id', TeacherClassDetail),
  protectedRoute('/student/assignments', StudentAssignments),
  tutorRoute('/tutor', ResponsableHome),
  tutorRoute('/tutor/enfant/$studentId', ResponsableChild),
  tutorRoute('/tutor/enfant/$studentId/parcours/$assignmentId', InteractiveParcours),
  tutorRoute('/tutor/enfant/$studentId/chat', StudentChat),
  tutorRoute('/tutor/enfant/$studentId/conversations', StudentConversations),
  protectedRoute('/conversations', Conversations),
  protectedRoute('/mini-apps', MiniApps),
  protectedRoute('/mini-apps/competences', Competences),
  protectedRoute('/mini-apps/cap', CapApp),
  protectedRoute('/mini-apps/fluence-direction', FluenceDirection),
  protectedRoute('/mini-apps/fluence-eleve', FluenceEleve),
  protectedRoute('/mini-apps/fluence-eleve-detail', FluenceEleveDetail),
  protectedRoute('/mini-apps/fluence-enseignant', FluenceEnseignant),
  protectedRoute('/mini-apps/francais-primaire', FrancaisPrimaireApp),
  protectedRoute('/mini-apps/hg-college-qcm', HgCollegeQcmApp),
  protectedRoute('/mini-apps/hg-college-enseignant', HgCollegeEnseignant),
  protectedRoute('/mini-apps/francais-college', FrancaisCollegeApp),
  protectedRoute('/mini-apps/francais-college/bilan', FrancaisCollegeBilanApp),
  protectedRoute('/mini-apps/francais-college/domaine/$domaine', FrancaisCollegeDomaineApp),
  protectedRoute('/mini-apps/francais-college/parcours/$slug', FrancaisCollegeHoteApp),
  protectedRoute('/mini-apps/francais-college/feuilleton', FrancaisCollegeFeuilletonApp),
  protectedRoute('/mini-apps/francais-college/feuilleton-2', FrancaisCollegeFeuilleton2App),
  protectedRoute('/mini-apps/francais-college/relecture-lexique', FrancaisCollegeRelectureLexiqueApp),
  protectedRoute('/mini-apps/francais-college/relecture-comprehension', FrancaisCollegeRelectureComprehensionApp),
  protectedRoute('/mini-apps/maths-primaire', MathsPrimaireApp),
  protectedRoute('/mini-apps/parcours', Parcours),
  adminRoute('/admin', Admin),
  adminRoute('/admin/users', AdminUsers),
  adminRoute('/admin/users/schools', AdminUsersSchools),
  adminRoute('/admin/users/schools/new', AdminUsersSchoolCreate),
  adminRoute('/admin/users/schools/$id', AdminUsersSchoolDetail),
  adminRoute('/admin/users/annuaire', AdminUsersAnnuaire),
  adminRoute('/admin/users/acces', AdminUsersAcces),
  adminRoute('/admin/chat', AdminChat),
  adminRoute('/admin/contenus', AdminContent),
  adminRoute('/admin/contenus/$id', AdminContentDetail),
  adminOrAiOperatorRoute('/admin/items', AdminItemsSearch),
  adminOrAiOperatorRoute('/admin/prompts', AdminPrompts),
  adminRoute('/admin/docs', AdminDocs),
  adminOrAiOperatorRoute('/admin/curriculum', AdminCurriculum),
  adminOrAiOperatorRoute('/admin/curriculum/ref', AdminCurriculumRef),
  adminOrAiOperatorRoute('/admin/programmes-manquants', AdminMissingProgrammes),
  adminOrAiOperatorRoute('/admin/couverture', AdminCouverture),
  adminOrAiOperatorRoute('/admin/curriculum/p/$id', AdminProgramme),
  adminOrAiOperatorRoute('/admin/llm-usage', AdminLlmUsage),
  adminOrAiOperatorRoute('/admin/conversations', AdminConversations),
  adminOrAiOperatorRoute('/admin/conversations/$id', AdminConversationDetail),
  adminOrAiOperatorRoute('/admin/safeguarding', AdminSafeguarding),
  adminOrAiOperatorRoute('/admin/safeguarding/$id', AdminSafeguardingDetail),
];

const testRoutesEnabled = import.meta.env.DEV || window.location.hostname.includes('staging');

const routeTree = rootRoute.addChildren(testRoutesEnabled ? [...routes, r('/__test/erreur', TestError)] : routes);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
